import React, { useState, useMemo } from 'react';
import { MoneySafe } from '../../types';
import Table from '../../components/Table/Table';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import SafeForm from '../../components/Form/SafeForm';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import { useCurrencies } from '../../hooks/useCurrencies';
import {
  useSafes,
  useCreateMoneySafe,
  useUpdateMoneySafe,
  useDeleteMoneySafe,
} from '../../hooks/useSafes';

const Safes: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingSafe, setEditingSafe] = useState<MoneySafe | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [balanceFromFilter, setBalanceFromFilter] = useState<string>('');
  const [balanceToFilter, setBalanceToFilter] = useState<string>('');

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  const { data: safes, isLoading, error, refetch } = useSafes(queryOptions);
  const { data: currencies = [] } = useCurrencies();

  const createMutation = useCreateMoneySafe(() => {
    setShowForm(false);
    setEditingSafe(undefined);
    setTimeout(() => refetch(), 100);
  });

  const updateMutation = useUpdateMoneySafe(() => {
    setShowForm(false);
    setEditingSafe(undefined);
    setTimeout(() => refetch(), 100);
  });

  const deleteMutation = useDeleteMoneySafe(() => {
    setTimeout(() => refetch(), 100);
  });

  const filteredSafes = useMemo(() => {
    if (!safes) return [];
    return safes.filter(safe => {
      const matchesSearch = !searchTerm || (
        safe.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        safe.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        safe.responsiblePerson.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesCurrency = currencyFilter === 'all' || resolveCurrencyCode(safe.currency, currencies) === currencyFilter;
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && safe.isActive) ||
        (statusFilter === 'inactive' && !safe.isActive);
      const matchesBalanceFrom = !balanceFromFilter || safe.balance >= parseFloat(balanceFromFilter);
      const matchesBalanceTo = !balanceToFilter || safe.balance <= parseFloat(balanceToFilter);
      return matchesSearch && matchesCurrency && matchesStatus && matchesBalanceFrom && matchesBalanceTo;
    });
  }, [safes, currencies, searchTerm, currencyFilter, statusFilter, balanceFromFilter, balanceToFilter]);

  const handleExport = () => {
    const exportData = filteredSafes.map(safe => ({
      'Safe Name': safe.name,
      'Location': safe.location,
      'Responsible Person': safe.responsiblePerson,
      'Balance': safe.balance,
      'Currency': resolveCurrencyCode(safe.currency, currencies),
      'Status': safe.isActive ? 'Active' : 'Inactive',
      'Created': new Date(safe.createdAt).toLocaleDateString()
    }));
    exportToExcel(exportData, `safe_boxes_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredSafes.map(safe => ({
      'Safe Name': safe.name,
      'Location': safe.location,
      'Responsible Person': safe.responsiblePerson,
      'Balance': safe.balance,
      'Currency': resolveCurrencyCode(safe.currency, currencies),
      'Status': safe.isActive ? 'Active' : 'Inactive',
      'Created': new Date(safe.createdAt).toLocaleDateString()
    }));
    printAsExcel(exportData, 'Safe Boxes');
  };

  const handleExportJson = () => {
    const dataStr = JSON.stringify(filteredSafes, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `safes_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filters: FilterConfig[] = [
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [
      { value: 'all', label: 'All Statuses' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }
    ]},
    { key: 'currency', label: 'Currency', type: 'select', value: currencyFilter, onChange: setCurrencyFilter, options: [
      { value: 'all', label: 'All Currencies' },
      ...(currencies as any[])
        .filter((c: any) => c.isActive !== false && c.code)
        .map((c: any) => ({ value: c.code, label: c.code }))
    ]},
    { key: 'balanceFrom', label: 'Min Balance', type: 'number', value: balanceFromFilter, onChange: setBalanceFromFilter, placeholder: '0.00', step: '0.01' },
    { key: 'balanceTo', label: 'Max Balance', type: 'number', value: balanceToFilter, onChange: setBalanceToFilter, placeholder: '999999.99', step: '0.01' },
  ];

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (value: string) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">{value}</div>
          </div>
        </div>
      )
    },
    { key: 'location', label: 'Location' },
    {
      key: 'currency',
      label: 'Currency',
      render: (value: any) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {resolveCurrencyCode(value, currencies)}
        </span>
      )
    },
    {
      key: 'balance',
      label: 'Balance',
      render: (value: number, row: MoneySafe) => {
        const currencyCode = resolveCurrencyCode(row.currency, currencies);
        return (
          <span className="text-sm font-medium text-gray-900">
            {currencyCode === 'USD' ? `$${value.toLocaleString()}` : `${value.toLocaleString()} ${currencyCode}`}
          </span>
        );
      }
    },
    { key: 'responsiblePerson', label: 'Responsible Person' },
    {
      key: 'isActive',
      label: 'Status',
      render: (value: boolean) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  const handleEdit = (safe: MoneySafe) => {
    setEditingSafe(safe);
    setShowForm(true);
  };

  const handleDelete = async (safe: MoneySafe) => {
    if (!window.confirm('Are you sure you want to delete this safe box?')) return;
    if (!safe?.id) return;
    try {
      await deleteMutation.execute(safe);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleAdd = () => {
    setEditingSafe(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<MoneySafe>) => {
    try {
      if (editingSafe?.id) {
        const updateData = { ...data, documentId: editingSafe.documentId };
        await updateMutation.execute({ id: editingSafe.id, data: updateData });
      } else {
        await createMutation.execute(data);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingSafe(undefined);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCurrencyFilter('all');
    setStatusFilter('all');
    setBalanceFromFilter('');
    setBalanceToFilter('');
  };

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message="Error loading safes. Please try again." onRetry={() => refetch()} entityName="safes" />;

  if (showForm) {
    return (
      <div className="space-y-6">
        <SafeForm safe={editingSafe} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Safe Boxes"
        description="Manage your company's safe boxes and cash storage"
        actions={[
          { label: 'Export JSON', onClick: handleExportJson, variant: 'info' },
        ]}
      />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search safe boxes..."
        filters={filters}
        onClearFilters={handleClearFilters}
        totalCount={safes?.length || 0}
        filteredCount={filteredSafes.length}
        entityName="safe boxes"
        onAdd={handleAdd}
        addLabel="Add Safe Box"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <div className="card">
        <Table
          columns={columns}
          data={filteredSafes}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAdd={handleAdd}
          title=""
          searchPlaceholder=""
        />
      </div>
    </div>
  );
};

export default Safes;
